import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useImmer } from 'use-immer';
import moment from 'moment';
import { planSlot, START_TOLERANCE_MS } from '../lib/schedule';
import { SERVER_FMT } from '../lib/clock';
import { itemSeconds, playableItems } from '../lib/items';
import { useLogger } from './useLogger';
import { useOnlineStatus } from './useOnlineStatus';

const IDLE = [];
const NO_ITEMS = [];

/**
 * Plays the current day's slots: activates the slot due at `localNow`, rotates the items of its
 * playlist that can play right now (known type, network if needed, inside their date window) and
 * calls `onSlotEnded` when it finishes (the caller re-evaluates the day from the bundle).
 */
export const usePlayback = ({ schedules, localNow, onSlotEnded }) => {
  const [state, setState] = useImmer({ activeItem: IDLE, currentIndex: 0 });
  const { LogError, LogToExternal } = useLogger();
  const online = useOnlineStatus();
  const onSlotEndedRef = useRef(onSlotEnded);
  onSlotEndedRef.current = onSlotEnded;

  // Activate the due slot and arm its start/end timers.
  useEffect(() => {
    let startTimer;
    let endTimer;

    try {
      const plan = planSlot(schedules, localNow);
      if (!plan) {
        setState((draft) => {
          draft.activeItem = IDLE;
        });
        return;
      }

      if (plan.startInMs > START_TOLERANCE_MS) {
        setState((draft) => {
          draft.activeItem = IDLE;
        });
      }

      startTimer = setTimeout(() => {
        setState((draft) => {
          draft.currentIndex = 0;
          draft.activeItem = plan.slot;
        });
      }, plan.startInMs);

      endTimer = setTimeout(() => {
        setState((draft) => {
          draft.currentIndex = 0;
        });
        onSlotEndedRef.current?.();
      }, plan.endInMs);
    } catch (error) {
      LogToExternal('error', error);
      LogError(1, `Exception:SetActiveScheduleHook ${error?.message}`);
    }

    return () => {
      clearTimeout(startTimer);
      clearTimeout(endTimer);
    };
  }, [schedules, localNow]);

  const dayKey = localNow
    ? moment(localNow, SERVER_FMT).format('YYYY-MM-DD')
    : undefined;
  const playlist = state.activeItem.playlist;
  const items = useMemo(
    () =>
      state.activeItem.scheduleId > 0
        ? playableItems(playlist, { online, dayKey })
        : NO_ITEMS,
    [state.activeItem, online, dayKey],
  );
  // The list shrinks when, say, the network drops: stay in range.
  const index = items.length ? state.currentIndex % items.length : 0;
  const currentItem = items[index];

  const advance = useCallback(() => {
    if (items.length < 2) return;
    setState((draft) => {
      draft.currentIndex = (index + 1) % items.length;
    });
  }, [items, index]);

  // One timer per item shown; a video playing to its end may advance sooner (`advance`).
  useEffect(() => {
    if (!currentItem || items.length < 2) return;
    const timer = setTimeout(
      advance,
      itemSeconds(currentItem, playlist) * 1000,
    );
    return () => clearTimeout(timer);
  }, [currentItem, advance]);

  return {
    activeItem: state.activeItem,
    currentIndex: index,
    items,
    currentItem,
    advance,
  };
};
